(() => {
  if (customElements.get('product-campaign-video')) return;

  class ProductCampaignVideo extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.button = this.querySelector('button');
      this.video = this.querySelector('video');
      this.slide = this.closest('.slider__item');
      this.visible = false;
      this.userPaused = this.userPaused || false;
      this.manualOptIn = this.manualOptIn || false;
      this.blocked = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.button.hidden = false;
      this.on(this.button, 'pointerdown', event => {
        this.pointer = { x: event.clientX, y: event.clientY };
        this.dragged = false;
      });
      this.on(this.button, 'pointermove', event => {
        if (this.pointer && Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y) > 10) {
          this.dragged = true;
        }
      });
      this.on(this.button, 'pointercancel', () => { this.dragged = true; });
      this.on(this.button, 'click', event => {
        if (event.detail && this.dragged) return;
        if (this.video && (!this.video.paused || this.pending)) {
          this.userPaused = true;
        } else {
          this.userPaused = false;
          this.manualOptIn = true;
          this.blocked = false;
        }
        this.sync();
      });
      this.on(document, 'visibilitychange', () => this.sync());
      this.on(document, 'on:media-gallery:change', () => this.sync());
      this.on(this.motion, 'change', () => this.sync());
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .2;
        this.sync();
      }, { threshold: [0, .2] });
      this.observer.observe(this);
      if (this.video) this.bindVideo();
      this.update();
    }

    on(target, name, callback) {
      target.addEventListener(name, callback, { signal: this.abort.signal });
    }

    eligible() {
      return this.visible && !document.hidden &&
        (!this.slide || this.slide.classList.contains('is-active')) &&
        !this.userPaused && !this.blocked &&
        (!this.motion.matches || this.manualOptIn);
    }

    ensureVideo() {
      if (this.video) return;
      const fragment = this.querySelector('template').content.cloneNode(true);
      this.insertBefore(fragment, this.button);
      this.video = this.querySelector('video');
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.controls = false;
      this.bindVideo();
    }

    bindVideo() {
      this.on(this.video, 'playing', () => {
        if (!this.eligible()) {
          this.video.pause();
          return;
        }
        this.classList.add('has-played');
        this.blocked = false;
        this.update();
      });
      this.on(this.video, 'pause', () => this.update());
      this.on(this.video, 'error', () => {
        this.blocked = true;
        this.pending = false;
        this.classList.remove('has-played');
        this.update();
      });
    }

    sync() {
      if (!this.eligible()) {
        this.video?.pause();
        this.update();
        return;
      }
      this.ensureVideo();
      if (!this.pending && this.video.paused) {
        this.pending = true;
        this.video.play().then(() => {
          this.pending = false;
          if (!this.eligible()) this.video.pause();
          this.update();
        }).catch(error => {
          this.pending = false;
          if (error.name !== 'AbortError' && this.eligible()) this.blocked = true;
          this.update();
          if (error.name === 'AbortError' && this.eligible()) {
            requestAnimationFrame(() => this.isConnected && this.sync());
          }
        });
      }
      this.update();
    }

    update() {
      const paused = this.userPaused || this.blocked || (this.motion.matches && !this.manualOptIn);
      this.toggleAttribute('data-paused', paused);
      this.button.setAttribute('aria-label', paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
      this.button.tabIndex = this.slide && !this.slide.classList.contains('is-active') ? -1 : 0;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      this.video?.pause();
      this.abort = null;
      this.pending = false;
    }
  }

  customElements.define('product-campaign-video', ProductCampaignVideo);
})();
